import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { Search, Package, X, AlertTriangle } from 'lucide-react';
import { brandForSku, categoryGlyphPath, GTM_LABELS, gtmColorVar } from './brand.js';
import { Glyph } from '../Glyph/Glyph.js';
const DRIVER_LABEL = {
    per_user: 'users',
    per_endpoint: 'endpoints',
    per_gb_day: 'GB/day of ingest',
    per_device: 'devices',
};
/** Drivers that scale with agency headcount — the ones the employee-count
 *  hint can seed. (per_gb_day is ingest volume, not headcount.) */
const SEAT_DRIVERS = new Set(['per_user', 'per_endpoint', 'per_device']);
/** "5,000-10,000" → 7500 · "10,000+" → 10000 · "<100" → 100 · rounds to
 *  a clean starting number. Returns null when the range has no digits. */
export function employeeMidpoint(range) {
    if (!range)
        return null;
    const clean = String(range).replace(/,/g, ''); // "5,000-10,000" → "5000-10000"
    const nums = (clean.match(/\d+/g) || []).map(Number).filter((n) => Number.isFinite(n));
    if (!nums.length)
        return null;
    let n;
    if (/\+/.test(clean))
        n = nums[nums.length - 1]; // "10000+" → floor
    else if (/^\s*[<≤]/.test(clean))
        n = nums[0]; // "<100" → ceiling
    else if (nums.length >= 2)
        n = (nums[0] + nums[1]) / 2; // range → midpoint
    else
        n = nums[0];
    return n >= 1000 ? Math.round(n / 100) * 100 : Math.round(n / 10) * 10;
}
/**
 * Parse spreadsheet-ish text with drop stats (audit QB-11): one line per
 * row; sku = the dash-bearing part-number cell, qty = a pure-integer cell
 * (default 1). `skipped` counts non-blank lines that yielded no SKU — the
 * caller surfaces it ("3 rows didn't parse") instead of dropping silently.
 * `header` is the count of lines skipped for looking like a column header,
 * kept separate so a heading line isn't reported as a parse failure.
 */
export function parseSkuPasteWithStats(text) {
    const out = [];
    let skipped = 0;
    for (const line of text.split(/\r?\n/)) {
        const cells = line.split(/\t|,|;/).map((c) => c.trim()).filter(Boolean);
        if (!cells.length)
            continue; // blank line — not a drop
        const sku = cells.find((c) => /^[A-Z0-9][A-Z0-9-]{4,}$/i.test(c) && c.includes('-'));
        if (!sku) {
            skipped++;
            continue;
        }
        const qtyCell = cells.find((c) => /^\d{1,5}$/.test(c));
        const desc = cells.filter((c) => c !== sku && c !== qtyCell).sort((a, b) => b.length - a.length)[0];
        out.push({ sku: sku.toUpperCase(), qty: qtyCell ? Number(qtyCell) : 1, description: desc });
    }
    return { items: out, skipped };
}
/** Back-compat: array-only result. */
export function parseSkuPaste(text) {
    return parseSkuPasteWithStats(text).items;
}
/**
 * Best-effort group title from a pasted sheet's heading line: the first
 * non-empty line qualifies when it is NOT one of the parsed SKU rows,
 * is a single cell (no tabs), is heading-length, and doesn't look like
 * a column-header row ("Part Number  Description  Qty"). Returns null
 * when unsure — the server falls back to "Pasted option".
 */
export function pasteGroupTitle(text, items) {
    const first = text.split(/\r?\n/).map((l) => l.trim()).find(Boolean);
    if (!first)
        return null;
    if (first.includes('\t'))
        return null;
    if (first.length < 3 || first.length > 60)
        return null;
    const upper = first.toUpperCase();
    if (items.some((i) => upper.includes(i.sku)))
        return null;
    if (/\b(qty|quantity|part|sku|description|price|unit|list|item)\b/i.test(first))
        return null;
    return first;
}
/**
 * The omnibox — ONE way to add anything (slice 3 of the redesign).
 *
 * Type = search (bundles + vehicle-priced products interleave). Empty +
 * focused = bundle starters and browse-category suggestions. ↑/↓ +
 * Enter adds and keeps focus for rapid entry; picking a bundle asks for
 * its one sizing number inline. ⌘K / Ctrl+K focuses from anywhere.
 * Docked above the lines; the panel opens downward.
 */
export function Omnibox({ query, results, loading, bundles, tree, browse, onBrowseSelect, onQueryChange, onAddSku, onAddBundle, onBulkAdd, onPasteReport, skuRules, iconsBaseUrl, disabled, autoFocus, sizingHint, gplDriftBySku, referenceOnly, formatCurrency, }) {
    const inputRef = React.useRef(null);
    const rootRef = React.useRef(null);
    const [focused, setFocused] = React.useState(false);
    const [forceBrowse, setForceBrowse] = React.useState(false);
    const [highlight, setHighlight] = React.useState(0);
    const [sizingFor, setSizingFor] = React.useState(null);
    const [sizingValue, setSizingValue] = React.useState('');
    // Land the cursor where work starts (skipped while disabled/hydrating).
    React.useEffect(() => {
        if (autoFocus && !disabled)
            inputRef.current?.focus();
    }, [autoFocus, disabled]);
    // ⌘K / Ctrl+K focuses the box from anywhere.
    React.useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);
    // Close on outside click.
    React.useEffect(() => {
        if (!focused)
            return undefined;
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setFocused(false);
                setForceBrowse(false);
                setSizingFor(null);
            }
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [focused]);
    const q = query.trim().toLowerCase();
    // Bundles only appear when the query matches one, and BELOW product
    // results — no default prominence (Ryan, field feedback 2026-07-04).
    const matchingBundles = q
        ? (bundles ?? []).filter((b) => q.split(/\s+/).every((tok) => b.name.toLowerCase().includes(tok)))
        : [];
    const shownBundles = matchingBundles.slice(0, 3);
    const items = [
        ...results.map((result) => ({ kind: 'sku', result })),
        ...shownBundles.map((bundle) => ({ kind: 'bundle', bundle })),
    ];
    // The panel never opens on bare focus/click — only when typing, when
    // a browse session is active, or via the explicit Browse button.
    const open = focused && (q.length > 0 || !!browse || forceBrowse);
    React.useEffect(() => setHighlight(0), [query, results, browse]);
    const addItem = (item) => {
        if (item.kind === 'sku') {
            onAddSku(item.result);
            onQueryChange('');
            inputRef.current?.focus();
        }
        else {
            setSizingFor(item.bundle);
            setSizingValue('');
        }
    };
    const commitBundle = () => {
        const size = Number(String(sizingValue).replace(/[,\s]/g, ''));
        if (!sizingFor || !Number.isFinite(size) || size <= 0)
            return;
        onAddBundle?.(sizingFor, size);
        setSizingFor(null);
        setSizingValue('');
        onQueryChange('');
        inputRef.current?.focus();
    };
    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            if (sizingFor)
                setSizingFor(null);
            else if (browse)
                onBrowseSelect?.(null);
            else {
                setFocused(false);
                inputRef.current?.blur();
            }
            return;
        }
        if (!items.length)
            return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, items.length - 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
        }
        else if (e.key === 'Enter' && items[highlight]) {
            e.preventDefault();
            addItem(items[highlight]);
        }
    };
    const browseCategory = browse ? tree?.find((c) => c.category === browse.category) : null;
    return (_jsxs("div", { className: "qb-omni", ref: rootRef, children: [open && (_jsx("div", { className: "qb-omni-pop", children: sizingFor ? (_jsxs("div", { className: "qb-bundle-sizing", children: [_jsxs("label", { children: [sizingFor.name, " \u2014 how many ", DRIVER_LABEL[sizingFor.sizingDriver ?? ''] || sizingFor.sizingDriver || 'units', "?", _jsx("input", { autoFocus: true, type: "number", min: 1, value: sizingValue, onChange: (e) => setSizingValue(e.target.value), onKeyDown: (e) => {
                                        if (e.key === 'Enter')
                                            commitBundle();
                                        if (e.key === 'Escape')
                                            setSizingFor(null);
                                    } })] }), _jsx("button", { type: "button", className: "qb-bundle-sizing-add", onClick: commitBundle, children: "Add" }), _jsx("button", { type: "button", className: "qb-bundle-sizing-cancel", onClick: () => setSizingFor(null), children: "Cancel" }), (() => {
                            if (!SEAT_DRIVERS.has(sizingFor.sizingDriver ?? ''))
                                return null;
                            const seats = employeeMidpoint(sizingHint?.employeeRange);
                            if (!seats)
                                return null;
                            const who = sizingHint?.account ? sizingHint.account : 'This agency';
                            return (_jsxs("div", { className: "qb-sizing-hint", children: [_jsxs("span", { children: [who, " has ~", _jsx("b", { children: sizingHint?.employeeRange }), " employees."] }), _jsxs("button", { type: "button", className: "qb-sizing-hint-use", onClick: () => setSizingValue(String(seats)), children: ["Use ", seats.toLocaleString()] }), _jsx("span", { className: "qb-sizing-hint-src", children: "agency headcount \u00B7 a starting estimate" })] }));
                        })()] })) : (_jsxs(_Fragment, { children: [(results.length > 0 || loading || q || browse) && (_jsxs("div", { className: "qb-omni-sec", children: [browse ? `${browse.category}${browse.model ? ` › ${browse.model}` : ''}` : 'Products on this contract', browse && (_jsx("button", { type: "button", className: "qb-omni-clear", "aria-label": "Clear browse filter", onClick: () => onBrowseSelect?.(null), children: _jsx(X, { size: 11, "aria-hidden": true }) }))] })), loading && _jsx("div", { className: "qb-omni-empty", children: "Searching\u2026" }), !loading && (q || browse) && results.length === 0 && (_jsx("div", { className: "qb-omni-empty", children: "No matches \u2014 try a shorter keyword." })), !loading && results.map((r, i) => {
                            const idx = i;
                            // Hover card: full description + prices + the contract
                            // file date the SKU was verified on — enough to confirm
                            // "right SKU, orderable" before adding it.
                            const hover = [
                                r.sku,
                                r.description,
                                [
                                    r.msrp !== null ? `List ${formatCurrency(r.msrp)}` : null,
                                    r.contractPrice !== null ? `Contract ${formatCurrency(r.contractPrice)}` : null,
                                ].filter(Boolean).join(' · '),
                                r.effectiveDate
                                    ? `✓ Verified on the contract price file — effective ${r.effectiveDate}`
                                    : null,
                            ].filter(Boolean).join('\n');
                            return (_jsxs("div", { role: "option", "aria-selected": highlight === idx, className: `qb-omni-item${highlight === idx ? ' is-highlighted' : ''}`, title: hover, onMouseEnter: () => setHighlight(idx), onClick: () => addItem({ kind: 'sku', result: r }), children: [(() => {
                                        const b = brandForSku(r.sku, skuRules);
                                        return b !== 'panw'
                                            ? _jsx("span", { className: "qb-gtm-tick", style: { background: gtmColorVar(b) }, title: `${GTM_LABELS[b] ?? b} — Palo Alto Networks` })
                                            : null;
                                    })(), _jsx("span", { className: "qb-omni-sku", children: r.sku }), (() => {
                                        const drift = gplDriftBySku?.[r.sku];
                                        if (!drift)
                                            return null;
                                        const label = drift.status === 'not_on_reference' ? 'off GPL'
                                            : drift.status === 'missing_from_contract' ? 'not on contract'
                                                : 'price drift';
                                        return (_jsxs("span", { className: `qb-gpl-drift qb-gpl-drift--${drift.status}`, title: drift.status === 'not_on_reference'
                                                ? 'Not on the current public PAN GPL — possibly end-of-sale; verify before quoting'
                                                : drift.status === 'missing_from_contract'
                                                    ? "On the current public PAN GPL but not on this contract's price list"
                                                    : 'List price differs from the current public PAN GPL', children: [_jsx(AlertTriangle, { size: 10, "aria-hidden": true }), label] }));
                                    })(), r.effectiveDate && !gplDriftBySku?.[r.sku] && (_jsx("span", { className: "qb-omni-verified", title: `Verified on the contract price file — effective ${r.effectiveDate}. The state can purchase this SKU.`, children: "\u2713" })), _jsx("span", { className: "qb-omni-desc", children: r.description }), _jsx("span", { className: "qb-omni-price", children: r.contractPrice !== null ? formatCurrency(r.contractPrice)
                                            : r.msrp !== null ? `${formatCurrency(r.msrp)} list` : '—' })] }, `${r.sku}-${i}`));
                        }), !loading && q && (referenceOnly?.length ?? 0) > 0 && (_jsxs(_Fragment, { children: [_jsx("div", { className: "qb-omni-sec qb-omni-sec--refonly", children: "On Palo Alto\u2019s latest GPL \u2014 not yet on this contract" }), referenceOnly.map((r) => (_jsxs("div", { role: "option", "aria-selected": false, className: "qb-omni-item qb-omni-item--refonly", title: [
                                        r.sku,
                                        r.description,
                                        r.listPrice !== null ? `GPL list ${formatCurrency(r.listPrice)}` : 'No list price published',
                                        `On the public PAN GPL${r.gplEffectiveDate ? ` (${r.gplEffectiveDate})` : ''} but NOT on this contract's price file yet — the state can't purchase it until it's uploaded. Click to add as a list-price placeholder.`,
                                    ].filter(Boolean).join('\n'), onClick: () => addItem({
                                        kind: 'sku',
                                        result: {
                                            sku: r.sku,
                                            description: r.description,
                                            productFamily: null,
                                            msrp: r.listPrice,
                                            contractPrice: null,
                                            discountCategory: null,
                                        },
                                    }), children: [_jsx("span", { className: "qb-omni-sku", children: r.sku }), _jsxs("span", { className: "qb-gpl-drift qb-gpl-drift--missing_from_contract", children: [_jsx(AlertTriangle, { size: 10, "aria-hidden": true }), r.newUpdatedFlag ? 'new — awaiting ETC' : 'awaiting ETC'] }), _jsx("span", { className: "qb-omni-desc", children: r.description }), _jsx("span", { className: "qb-omni-price", children: r.listPrice !== null ? `${formatCurrency(r.listPrice)} list` : '—' })] }, `ref-${r.sku}`)))] })), shownBundles.length > 0 && (_jsxs(_Fragment, { children: [_jsx("div", { className: "qb-omni-sec", children: "Bundles" }), shownBundles.map((b, i) => (_jsxs("div", { role: "option", "aria-selected": highlight === (results.length + i), className: `qb-omni-item${highlight === (results.length + i) ? ' is-highlighted' : ''}`, onMouseEnter: () => setHighlight(results.length + i), onClick: () => addItem({ kind: 'bundle', bundle: b }), children: [iconsBaseUrl
                                            ? _jsx(Glyph, { url: iconsBaseUrl + 'ai/precision-ai-security-bundle.svg', size: 14 })
                                            : _jsx(Package, { size: 13, "aria-hidden": true }), _jsx("span", { className: "qb-omni-name", children: b.name }), _jsxs("span", { className: "qb-omni-tag", children: ["sized by ", DRIVER_LABEL[b.sizingDriver ?? ''] || b.sizingDriver || 'units'] })] }, b.bundleId)))] })), onBrowseSelect && tree && tree.length > 0 && (_jsxs("div", { className: "qb-omni-crumbs", children: [_jsx("span", { className: "qb-omni-crumbs-lbl", children: "Browse:" }), (browseCategory ? browseCategory.models.slice(0, 10) : tree.slice(0, 7)).map((n) => 'models' in n ? (_jsxs("button", { type: "button", className: "qb-omni-crumb", onClick: () => onBrowseSelect({ category: n.category, model: null }), children: [(() => {
                                            const gp = iconsBaseUrl ? categoryGlyphPath(n.category) : null;
                                            return gp ? _jsx(Glyph, { url: iconsBaseUrl + gp, size: 13 }) : null;
                                        })(), n.category, " \u203A"] }, n.category)) : (_jsx("button", { type: "button", className: "qb-omni-crumb", onClick: () => onBrowseSelect({ category: browse.category, model: n.model }), children: n.model }, n.model)))] }))] })) })), _jsxs("div", { className: `qb-omni-input${focused ? ' is-focused' : ''}`, children: [iconsBaseUrl
                        ? _jsx(Glyph, { url: iconsBaseUrl + 'analysis/magnifier.svg', size: 15 })
                        : _jsx(Search, { size: 14, "aria-hidden": true }), _jsx("input", { ref: inputRef, type: "search", value: query, disabled: disabled, placeholder: disabled ? 'Loading quote…' : 'Add anything — search products & bundles, browse, or paste SKUs from a sheet…', "aria-label": "Add products", onFocus: () => setFocused(true), onChange: (e) => onQueryChange(e.target.value), onKeyDown: onKeyDown, onPaste: (e) => {
                            if (!onBulkAdd)
                                return;
                            const text = e.clipboardData.getData('text');
                            if (!/[\n\t]/.test(text))
                                return; // single value: paste normally
                            const { items, skipped } = parseSkuPasteWithStats(text);
                            if (items.length >= 1) {
                                e.preventDefault();
                                const title = pasteGroupTitle(text, items);
                                onBulkAdd(items, title);
                                onQueryChange('');
                                // A detected heading line counts as skipped in the parser; don't
                                // report it as a parse failure.
                                if (onPasteReport)
                                    onPasteReport(items.length, Math.max(0, title ? skipped - 1 : skipped));
                            }
                        } }), _jsx("button", { type: "button", className: "qb-omni-browse", disabled: disabled, "aria-expanded": forceBrowse, title: "Browse the catalog by category, and bundles", onClick: () => { setForceBrowse((v) => !v); setFocused(true); inputRef.current?.focus(); }, children: "Browse" }), _jsx("kbd", { children: "\u2318K" })] })] }));
}
